import type * as React from 'react';

import * as api from '@gitbook/api-client';
import { Badge, type BadgeProps, Tooltip } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { CHANGE_REQUEST_STATUS_ICONS } from './ChangeRequestIcon';

/**
 * Badge to represent the status of a change request.
 */
export function ChangeRequestStatusBadge(
    props: {
        changeRequest: api.ChangeRequest;
        withIcon?: boolean;
    } & BadgeProps
): React.ReactElement {
    const { changeRequest, withIcon = true, ...rest } = props;
    const label = getChangeRequestStatusDescription(changeRequest.status);

    return (
        <Tooltip label={getTooltip(changeRequest)}>
            <Badge
                {...rest}
                {...statusToBadgeProps[changeRequest.status]}
                label={label}
                icon={withIcon ? CHANGE_REQUEST_STATUS_ICONS[changeRequest.status] : undefined}
            />
        </Tooltip>
    );
}

/**
 * Returns a user-friendly description of a change request based on its status.
 */
export function getChangeRequestStatusDescription(status: api.ChangeRequestStatus): string {
    switch (status) {
        case api.ChangeRequestStatus.Open:
            return 'In review';
        case api.ChangeRequestStatus.Archived:
            return 'Archived';
        case api.ChangeRequestStatus.Merged:
            return 'Merged';
        case api.ChangeRequestStatus.Draft:
            return 'Draft';
        default:
            assertNever(status);
    }
}

function getTooltip(changeRequest: api.ChangeRequest): string | null {
    switch (changeRequest.status) {
        case api.ChangeRequestStatus.Draft:
            return 'This change request is currently a draft';
        case api.ChangeRequestStatus.Open:
            return 'This change request has been submitted for review';
        case api.ChangeRequestStatus.Merged:
            return 'This change request has been merged into the space';
        case api.ChangeRequestStatus.Archived:
            return 'This change request has been archived';
        default:
            assertNever(changeRequest.status);
    }
}

const statusToBadgeProps: { [status in api.ChangeRequestStatus]: BadgeProps } = {
    [api.ChangeRequestStatus.Draft]: { kind: 'secondary' },
    [api.ChangeRequestStatus.Open]: { kind: 'info' },
    [api.ChangeRequestStatus.Merged]: { kind: 'merge' },
    [api.ChangeRequestStatus.Archived]: { kind: 'danger' },
};
